
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  BaseProfileDataInterface,
  ProfileInterface,
  ProfileRepositoryInterface,
} from '@/services/Users-profile/interfaces';
import { LeaveRequestInterface, LeavesRepositoryInterface } from '@/services/Calendar/leaves/interfaces';
import { DataTableHeader } from 'vuetify';
import DepartmentRepositoryInterface, { DepartmentInterface } from '@/services/Departments/Interfaces';
import { UserRepoInterface } from '@/services/Users/Interfaces';
import ViewLeaveRequestDialog from '@/components/dialogs/calendar/leave/ViewLeaveRequestDialog.vue';

import Docxtemplater from 'docxtemplater';
import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';
import saveAs from 'file-saver';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ViewLeaveRequestDialog,
  },
  inject: [
    'leavesRepository',
    'profileRepository',
    'departmentRepository',
    'userRepository',
  ],
})
export default class Archive extends Vue {
  private leavesRepository!: LeavesRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private departmentRepository!: DepartmentRepositoryInterface;
  private userRepository!: UserRepoInterface;

  private loading: boolean = true;
  private itemsPerPage: number = 10;
  private currentPage: number = 1;
  private totalItems: number = 0;
  private department: string | null = null;

  private leaves: LeaveRequestInterface[] = [];
  private departments: DepartmentInterface[] = [];
  private usersIds: string[] = [];

  private viewLeaveRequestDialog: string = '';
  private leaveRequestForDialog!: LeaveRequestInterface;

  private usersMap: { [userId: string]: BaseProfileDataInterface } = {};
  private headers: DataTableHeader[] = [
    { text: 'Type', value: 'leaveType.name', sortable: false },
    { text: 'Days', value: 'days', sortable: false },
    { text: 'Dates', value: 'date', sortable: false },
    { text: 'Submitted By', value: 'requestBy', sortable: false },
    { text: 'Reviewed By', value: 'reviewedBy', sortable: false },
    { text: 'Requested On', value: 'requestOn', sortable: false },
    { text: 'Status', value: 'status', sortable: false },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  private monthMenu: boolean = false;
  private selectedMonth: string | null = null; // 'YYYY-MM'

  public async created(): Promise<void> {
    await this.initialize();
    await this.getDepartments();
  }

  private async initialize(): Promise<void> {
    this.loading = true;
    const [leaves, totalItems] = await this.getLeaves();
    await this.getUsers(leaves);

    this.leaves = leaves;
    this.totalItems = totalItems;
    this.loading = false;
  }

  private async getLeaves(): Promise<[LeaveRequestInterface[], number]> {
    if (this.usersIds.length) {
      return this.leavesRepository.getArchive(this.currentPage, this.usersIds);
    }

    return this.leavesRepository.getArchive(this.currentPage);
  }

  private async getUsers(leaves: LeaveRequestInterface[]): Promise<void> {
    const usersIds: string[] = [];
    leaves.forEach((leave) => {
      if (!usersIds.includes(leave.requestBy)) {
        usersIds.push(leave.requestBy);
      }

      if (!!leave.reviewedBy && !usersIds.includes(leave.reviewedBy)) {
        usersIds.push(leave.reviewedBy);
      }
    });

    const usersProfiles = await this.profileRepository.getBaseProfileDataByUsersIds(usersIds);
    usersProfiles.forEach((profile) => {
      this.usersMap[profile.userId] = profile;
    });
  }

  private async getDepartments(): Promise<void> {
    this.departments = await this.departmentRepository.getAllDepartments();
  }

  private async changeDepartment(): Promise<void> {
    if (this.department) {
      const users = await this.userRepository.getUsersByDepartment(this.department);
      this.usersIds = users.map((user) => String(user.id));
    } else {
      this.usersIds = [];
    }

    await this.initialize();
  }

  private async changePage(): Promise<void> {
    await this.initialize();
  }

  private viewLeaveRequest(leaveRequest: LeaveRequestInterface): void {
    this.viewLeaveRequestDialog = 'view-leave-request-dialog';
    this.leaveRequestForDialog = leaveRequest;
  }

  private getLeaveStatus(leaveRequest: LeaveRequestInterface): string {
    if (leaveRequest.cancellation && leaveRequest.cancellation.isApproved) {
      return 'Cancelled';
    }

    if (leaveRequest.isApproved === null) {
      return 'Pending';
    } else if (leaveRequest.isApproved) {
      return 'Approved';
    } else {
      return 'Denied';
    }
  }

  private get selectedMonthLabel(): string {
    if (!this.selectedMonth) {
      return '';
    }
    const [year, month] = this.selectedMonth.split('-').map(Number);
    const d = new Date(year, month - 1, 1);
    return d.toLocaleString('default', { month: 'long', year: 'numeric' });
  }

  // Leaves for selected month (based on dateFrom)
  private getLeavesForSelectedMonth(): LeaveRequestInterface[] {
    if (!this.selectedMonth) {
      return [];
    }

    const [yearStr, monthStr] = this.selectedMonth.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);

    return this.leaves.filter((leave) => {
      const d = new Date(leave.dateFrom);
      return d.getFullYear() === year && (d.getMonth() + 1) === month;
    });
  }

  private generateLeaveDocumentNumber(request: LeaveRequestInterface): string {
    const serialNumber = '0'.repeat(6 - String(request.id).length) + request.id;
    return `${request.leaveType.name[0]}${serialNumber}`;
  }

  private generateLeaveDocumentName(request: LeaveRequestInterface): string {
    const profile = this.usersMap[request.requestBy];
    const serialNumber = this.generateLeaveDocumentNumber(request);
    const name = profile?.fullName
      ? profile.fullName
      : `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim();

    return `${serialNumber} - ${name} - ${request.dateFrom}`;
  }

  // Build a single DOCX blob for one leave request
  private async buildLeaveDocumentBlob(
    leaveRequest: LeaveRequestInterface,
    userProfile: ProfileInterface,
  ): Promise<{ blob: Blob; filename: string }> {
    const dataset = {
      requestNumber: this.generateLeaveDocumentNumber(leaveRequest),
      realName: `${userProfile.personalDetails.firstName} ${userProfile.personalDetails.lastName}`,
      department: userProfile.companyDetails.department,
      leaveDays: leaveRequest.days,
      leaveType: leaveRequest.leaveType.translatedName,
      startDate: new Date(leaveRequest.dateFrom).toLocaleString('en-gb').substr(0, 10),
      endDate: new Date(leaveRequest.dateTo).toLocaleString('en-gb').substr(0, 10),
      requestedOn: new Date(leaveRequest.requestOn).toLocaleString('en-gb').substr(0, 10),
      article: leaveRequest.leaveType.article ? leaveRequest.leaveType.article : '',
    };

    const content: any = await new Promise((resolve, reject) => {
      JSZipUtils.getBinaryContent(
        '/document-templates/leaveRequestTemplate.docx',
        (error: any, data: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        },
      );
    });

    const zip = JSZip(content);
    const doc = new Docxtemplater().loadZip(zip);
    doc.setData(dataset);

    try {
      doc.render();
    } catch (error) {
      const e = {
        message: error.message,
        name: error.name,
        stack: error.stack,
        properties: error.properties,
      };
      console.log(JSON.stringify({ error: e }));
      throw error;
    }

    const blob: Blob = doc.getZip().generate({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const filename = this.generateLeaveDocumentName(leaveRequest);

    return { blob, filename };
  }

  // Download all documents for selected month as a single ZIP
  private async downloadMonthZip(): Promise<void> {
    if (!this.selectedMonth) {
      this.$errorDialog?.show?.('Please select a month first');
      return;
    }

    const leavesForMonth = this.getLeavesForSelectedMonth();

    if (!leavesForMonth.length) {
      this.$errorDialog?.show?.('There are no leave requests for this month on this page.');
      return;
    }

    this.loading = true;

    try {
      const zip = JSZip();
      const userProfile = await this.profileRepository.getOneByUserId(this.$store.getters.id);

      for (const leave of leavesForMonth) {
        const { blob, filename } = await this.buildLeaveDocumentBlob(leave, userProfile);

        const buffer = await blob.arrayBuffer();
        zip.file(`${filename}.docx`, buffer);
      }

      const [yearStr, monthStr] = this.selectedMonth.split('-');
      const zipBlob = await zip.generate({ type: 'blob' });
      const zipName = `leave-documents-${monthStr}-${yearStr}.zip`;

      saveAs(zipBlob, zipName);
    } catch (e) {
      this.$errorDialog?.show?.('Could not generate ZIP. Please try again.');
      console.error(e);
    } finally {
      this.loading = false;
    }
  }
}
